import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Menu } from 'lucide-react';
import Sidebar from '@/components/layout/Sidebar';
import AdminGateOverlay from '@/components/layout/AdminGateOverlay';
import ProfileModal from '@/components/profile/ProfileModal';
import QuestModal from '@/components/quests/QuestModal';
import SubmissionCategoryModal from '@/components/submit/SubmissionCategoryModal';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/lib/utils';

export function AppLayout() {
  const adminGateRole = useAuthStore((s) => s.adminGateRole);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Lock body scroll + ESC-to-close while the drawer is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [mobileOpen]);

  if (adminGateRole) {
    return (
      <div className="surface-app flex min-h-screen items-center justify-center p-4">
        <AdminGateOverlay />
      </div>
    );
  }

  return (
    <div className="surface-app flex min-h-screen">
      {/* Desktop sidebar — always rendered, hidden below lg */}
      <div className="hidden lg:flex sticky top-0 h-screen self-start">
        <Sidebar />
      </div>

      {/* Mobile drawer */}
      <div
        className={cn(
          'fixed inset-0 z-50 lg:hidden',
          mobileOpen ? 'pointer-events-auto' : 'pointer-events-none',
        )}
        aria-hidden={!mobileOpen}
      >
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
          className={cn(
            'absolute inset-0 bg-ink-950/30 backdrop-blur-sm transition-opacity duration-200',
            mobileOpen ? 'opacity-100' : 'opacity-0',
          )}
          tabIndex={mobileOpen ? 0 : -1}
        />
        <div
          className={cn(
            'absolute inset-y-0 left-0 transition-transform duration-200 ease-out',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <Sidebar onNavigate={() => setMobileOpen(false)} />
        </div>
      </div>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile-only menu trigger. The desktop sidebar is always visible, so
            this is the only chrome outside it — no title or connect, those live
            in the page body and the sidebar foot respectively. */}
        <div className="sticky top-0 z-30 flex items-center bg-paper/80 px-4 py-3 backdrop-blur-md lg:hidden">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation menu"
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-white text-ink-950 transition-colors hover:bg-paper-warm"
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <main className="mx-auto w-full max-w-5xl px-6 py-8 lg:px-10 lg:py-10">
          <Outlet />
        </main>
      </div>

      <ProfileModal />
      <QuestModal />
      <SubmissionCategoryModal />
    </div>
  );
}

export default AppLayout;
